import React, { useEffect, useRef, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';

import { useStyles } from './GradingEditor.styles'
import DownIcon from '@assets/down-icon.svg';
import UpIcon from '@assets/up-icon.svg';
import CompletedIcon from '@assets/checkmark.svg'

import { useExecutionsGridUIStore, useExecutionsUIStore, useGradingsUIStore, useUserProfileUIStore } from '@core/useStores';

import { ThemeProps } from '@styles/theme';
import {
    GRADES, GRADES_OPTIONS,
    SAFETY_GRADES, SAFETY_GRADES_OPTIONS,
    GradingBehaviour,
    GradingCompetency,
    GradingDraftValue,
    GradingDraftSelection,
    GradingStep,
    getGradingStepLabel,
    getNormalizedGradingValue,
    toSpaced,
    getGradingColor} from 'NeoAnalytics/stores/GradingsUI.store';
import classNames from 'classnames';
import { Button } from '@shared/components/Button';
import { toast } from 'react-toastify';
import { ToastMessage } from '@shared/components/Toast/ToastMessage';

export function toAcronym(str: string): string {
    return str
        .trim()
        .split(/\s+/)
        .map(word => word[0])
        .join("")
        .toUpperCase();
}

const compareStepIds = (left: number | string, right: number | string): number => {
    const leftNumber = Number(left);
    const rightNumber = Number(right);

    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
        return leftNumber - rightNumber;
    }

    return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
};

type CustomCheckboxProps = {
  checked: boolean;
  label?: string;
  uncheckedBgColor?: string;
  onClick?: (id: string) => void;
  disabled?: boolean;
  id?: string;
  compCheck?: boolean;
};

export const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
    checked,
    onClick,
    disabled = false,
    compCheck = false,
    id,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const inputId = id ?? React.useId();

    return (
        <label
            htmlFor={inputId}
            className={classNames(
                styles.ccWrapper,
                styles.ccWrapperFocusWithin,
                disabled && styles.ccWrapperDisabled
            )}
        >
            <input
                id={inputId}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                className={styles.ccInput}
                onClick={() => onClick ? onClick(inputId) : {}}
            />

            {compCheck &&
            <span
                className={classNames(
                    styles.ccBox,
                    compCheck ? (checked ? styles.ccCompBoxChecked : styles.ccCompBoxUnchecked) : (checked ? styles.ccBoxChecked : styles.ccBoxUnchecked)
                )}
                aria-hidden="true"
            >
                {checked ? "✓" : "✕"}
            </span>
            }
            {!compCheck &&
            <span
                className={classNames(
                    styles.ccBox,
                    checked ? styles.ccBoxChecked : styles.ccBoxUnchecked
                )}
                aria-hidden="true"
            >
                {checked ? "✓" : ""}
            </span>
            }
        </label>
    );
};

export function BehaviourRow({ b, onStepCheck }: {
    b: GradingBehaviour,
    onStepCheck?: (obId:string, stepIndex: number) => void}) {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const detectedCount = b.steps.filter(s => s.obDetected).length;
    const hasChildren = b.steps.length > 0;
    const toggleOpen = () => {
        if (hasChildren) {
            setIsOpen(value => !value);
        }
    };
    const toggleStep = (stepIndex: number) => {
        if (onStepCheck) {
            onStepCheck(b.obId, stepIndex);
        }
    };


    return (
        <div className={styles.behaviourContainer}>
            <button
                type="button"
                className={classNames(
                    styles.behaviourHeader,
                    hasChildren && styles.behaviourHeaderInteractive
                )}
                onClick={toggleOpen}
                disabled={!hasChildren}
            >
                <CustomCheckbox
                    checked={detectedCount > 0}
                    disabled
                    compCheck
                />

                <div className={styles.titleContainer}>{b.obName}</div>

                {hasChildren && (
                    <div className={styles.behaviourCounter}>
                        <span className={styles.behaviourCountBadge}>{`${detectedCount}/${b.steps.length}`}</span>
                        <img
                            src={isOpen ? UpIcon : DownIcon}
                            className={styles.behaviourToggleIcon}
                        />
                    </div>
                )}
            </button>

            {isOpen && (
                <div className={styles.behaviourDataContainer}>
                    {b.steps.length > 0 && b.steps.map((s, stepIndex)=>{
                        return(
                            <div
                                key={stepIndex}
                                className={styles.behaviourStepContainer}
                                role={onStepCheck ? 'button' : undefined}
                                tabIndex={onStepCheck ? 0 : -1}
                                onClick={() => toggleStep(stepIndex)}
                                onKeyDown={event => {
                                    if ((event.key === 'Enter' || event.key === ' ') && onStepCheck) {
                                        event.preventDefault();
                                        toggleStep(stepIndex);
                                    }
                                }}
                            >
                                <CustomCheckbox
                                    checked={s.obDetected}
                                    disabled
                                />

                                <div className={styles.obValueContainer}>{getGradingStepLabel(s)}</div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    );
}

type GradingEditorProps = {
    onCancel: () => void;
    hasGrading?: boolean;
};

export const GradingEditor: React.FC<GradingEditorProps> = ({onCancel, hasGrading = false}) =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const gradingsUIStore = useGradingsUIStore();
    const executionsGridUIStore = useExecutionsGridUIStore();
    const executionUIStore = useExecutionsUIStore();
    const userProfileUIStore = useUserProfileUIStore();
    const executionId = executionUIStore.selectedExecutionId ?? executionUIStore._execution.id ?? '';

    const [competencies, setCompetencies] = useState<(GradingCompetency)[]>();

    const [activeComp, setActiveComp] = useState<GradingCompetency>();
    const [showRecap, setShowRecap] = useState<boolean>(false);

    const [gradings, setGradings] = useState<Record<number, GradingDraftValue>>({});

    // True once the user has made at least one change. Drafts are only persisted
    // per session while dirty — an untouched editor leaves no draft behind.
    const [dirty, setDirty] = useState<boolean>(false);
    // Holds the in-progress (un-committed) selection restored from a draft, applied
    // once the matching competency becomes active so the per-competency sync effect
    // doesn't clobber it.
    const pendingRestoreSelectionRef = useRef<GradingDraftSelection | null>(null);

    const findGroupedGradingValue = (competencyId: number) => {
        return gradingsUIStore.groupedBehaviours?.gradingValues?.find(
            value => getNormalizedGradingValue(value).compId === competencyId
        );
    };

    const resolveInitialCompetencies = () => {
        const execution = executionUIStore._execution;

        if (!execution || !['assessment', 'feedback'].includes(execution.executionMode?.toLowerCase() ?? '')) {
            return [] as GradingCompetency[];
        }

        if (hasGrading) {
            return gradingsUIStore.groupedBehaviours?.formattedComps ?? [];
        }

        return gradingsUIStore.loadOBsVariation(execution.entityName ?? execution);
    };

    useEffect(() =>{
        const draft = executionId ? gradingsUIStore.getEditorDraft(executionId) : null;

        if (draft) {
            setCompetencies(draft.competencies);
            setGradings(draft.gradings);
            setShowRecap(draft.showRecap);
            setDirty(true);

            if (draft.selection) {
                pendingRestoreSelectionRef.current = draft.selection;
                setSelectedGrading(draft.selection.grade);
                setSelectedSafetyGrade(draft.selection.safetyGrade);
                setNotes(draft.selection.notes ?? '');
            } else {
                pendingRestoreSelectionRef.current = null;
            }

            if (draft.activeCompId) {
                const draftActive = draft.competencies.find(comp => comp.id === draft.activeCompId);
                setActiveComp(draftActive);
            } else {
                setActiveComp(draft.competencies[0]);
            }

            return;
        }

        const nextCompetencies = resolveInitialCompetencies();
        setCompetencies(nextCompetencies);
        setGradings({});
        setShowRecap(false);
        setDirty(false);
        pendingRestoreSelectionRef.current = null;
        setActiveComp(nextCompetencies[0]);
    }, [
        executionId,
        hasGrading,
        executionUIStore._execution?.id,
        executionUIStore._execution?.entityName,
        executionUIStore._execution?.entityVersion,
        executionUIStore._execution?.entityId,
        executionUIStore._execution?.executionMode,
        gradingsUIStore.grading.id,
        gradingsUIStore.grading.updatedAt,
        gradingsUIStore.grading.reviews.length,
        gradingsUIStore.gradingFound,
    ])

    useEffect(() =>{
        if(competencies && competencies.length > 0 && (!activeComp || !competencies.some(comp => comp.id === activeComp.id))){
            setActiveComp(competencies[0]);
        }
    },[competencies])

    useEffect(() =>{
        if(activeComp){
            const pendingSelection = pendingRestoreSelectionRef.current;
            if (pendingSelection && pendingSelection.compId === activeComp.id) {
                pendingRestoreSelectionRef.current = null;
                setSelectedGrading(pendingSelection.grade);
                setSelectedSafetyGrade(pendingSelection.safetyGrade);
                setNotes(pendingSelection.notes ?? '');

                return;
            }

            if(hasGrading){
                const saveCompGrading = Object.entries(gradings).find(g => +g[0] == activeComp.id);
                const compGrading = findGroupedGradingValue(activeComp.id);
                const normalizedCompGrading = getNormalizedGradingValue(compGrading);
                const note = gradingsUIStore.groupedBehaviours?.notes?.find(n => n.compId == activeComp.id);

                setSelectedGrading(saveCompGrading ? saveCompGrading[1].grade : normalizedCompGrading.grade);
                setSelectedSafetyGrade(saveCompGrading ? saveCompGrading[1].safetyGrade : normalizedCompGrading.safetyGrade);
                setNotes((saveCompGrading && saveCompGrading[1].notes) ? saveCompGrading[1].notes : (note ? note?.text : ''));
            }else{
                const compGrading = Object.entries(gradings).find(g => +g[0] == activeComp.id);

                setSelectedGrading(compGrading ? compGrading[1].grade : undefined);
                setSelectedSafetyGrade(compGrading ? compGrading[1].safetyGrade : undefined);
                setNotes((compGrading && compGrading[1].notes) ? compGrading[1].notes : '');
            }
        }
    },[activeComp])

    const setCompGrading = (id: number, safety: SAFETY_GRADES | undefined, grade: GRADES | undefined, notes: string) =>{
        setGradings(current => {
            const next = { ...current };
            if (safety == undefined && grade == undefined && !notes.trim()) {
                delete next[id];

                return next;
            }
            next[id] = { safetyGrade: safety, grade: grade, notes: notes };

            return next;
        });
    }

    const getCompetencyDraft = (competencyId: number): GradingDraftValue | undefined => {
        const localDraft = gradings[competencyId];
        if (localDraft) {
            return localDraft;
        }

        if (hasGrading) {
            const gradingValue = findGroupedGradingValue(competencyId);
            const normalizedGradingValue = getNormalizedGradingValue(gradingValue);
            const noteValue = gradingsUIStore.groupedBehaviours?.notes?.find(note => note.compId === competencyId);

            if (gradingValue || noteValue) {
                return {
                    safetyGrade: normalizedGradingValue.safetyGrade,
                    grade: normalizedGradingValue.grade,
                    notes: noteValue?.text,
                };
            }
        }

        return undefined;
    };

    const isCompetencyGraded = (competencyId: number): boolean => {
        const draft = getCompetencyDraft(competencyId);

        return draft?.grade != undefined && draft?.safetyGrade != undefined;
    };

    const saveGrading = async () => {
        const obBySteps: GradingStep[] = [];
        let notes = '';
        const gradingsValues: Record<string,any> = {};
        const actorId = executionUIStore._execution.actorIds?.[0] ?? '';

        competencies?.map(c => {
            c?.behaviours.map(b =>{
                b.steps.map(s => {
                    if(obBySteps.find(st => st.stepId == s.stepId)){
                        const foundStep = obBySteps.find(st => st.stepId == s.stepId);
                        foundStep?.observableBehaviours.push(
                            {
                                obId: b.obId,
                                obDescription: b.obDescription,
                                obDashboardName: s.obDashboardName,
                                obAdaptedDescription: b.obAdaptedDescription,
                                obDetected: s.obDetected,
                            }
                        )
                    }else{
                        const stepData: GradingStep = {
                            stepId: s.stepId,
                            stepName: s.stepName,
                            stepDetectionTimestampVideoStart: s.stepDetectionTimestampVideoStart,
                            stepDetectionTimestampVideoEnd: s.stepDetectionTimestampVideoEnd,
                            stepDetected: true,
                            observableBehaviours: [
                                {
                                    obId: b.obId,
                                    obDescription: b.obDescription,
                                    obAdaptedDescription: b.obAdaptedDescription,
                                    obDashboardName: s.obDashboardName,
                                    obDetected: s.obDetected,
                                }
                            ],
                        }

                        obBySteps.push(stepData)
                    }
                })
            })

            const grading = getCompetencyDraft(c?.id ?? -1);
            if(grading && grading.grade != undefined && grading.safetyGrade != undefined){
                gradingsValues[c?.id ?? -1] = {
                    compId: c?.id,
                    grading: grading.grade,
                    safetyGrade: grading.safetyGrade,
                }
                notes = notes.concat(`comp ${c?.id}: ${grading.notes ?? ''}||`);
            }
        })

        const sorted = obBySteps.sort((a, b) => compareStepIds(a.stepId, b.stepId));
        const grades = Object.entries(gradingsValues).map((g: any) => g[1]);

        if(hasGrading){

            const gradingData = {
                reviewerId: userProfileUIStore.userInfo.id,
                score: 0,
                feedback: notes,
                details: {
                    procedure_steps: sorted,
                    grading_values: grades,
                },
            }

            const result = await gradingsUIStore.postReview(gradingData);

            if (!result.success) {
                toast.error(
                    <ToastMessage
                        message={"Couldn't Create Review"}
                        type='error'
                    />
                );
            } else {
                toast.success(
                    <ToastMessage
                        message={"Review Successfully Created"}
                        type='success'
                    />
                );
            }
        }else{

            const gradingData = {
                executionId: executionUIStore._execution.id,
                actorId,
                score: 0,
                feedback: notes,
                details: {
                    procedure_steps: sorted,
                    grading_values: grades,
                },
            }

            const result = await gradingsUIStore.postGrading(gradingData);

            if (!result.success) {
                toast.error(
                    <ToastMessage
                        message={"Couldn't Create Grading"}
                        type='error'
                    />
                );
            } else {
                toast.success(
                    <ToastMessage
                        message={"Grading Successfully Created"}
                        type='success'
                    />
                );
            }
        }
        if(hasGrading){
            gradingsUIStore.toggleEdit(false);
        }else{
            gradingsUIStore.toggleGradingFound(true);
        }
        if (executionId) {
            gradingsUIStore.clearEditorDraft(executionId);
        }

        if (executionUIStore.selectedExecutionId) {
            await Promise.all([
                gradingsUIStore.loadGradings({executionIds: [executionUIStore.selectedExecutionId]}),
                executionsGridUIStore.loadGradingSummaries([executionUIStore.selectedExecutionId]),
            ]);
        }
    }

    const goToNext = () => {
        if(competencies){
            if(!showRecap){
                const idx = competencies.findIndex(co => co?.id == activeComp?.id);
                setCompGrading(activeComp!.id, selectedSafetyGrade, selectedGrade, notes);
                if(idx  < competencies.length - 1){
                    setActiveComp(competencies[idx + 1]);
                }else{
                    setShowRecap(true);
                }
            }
        }
    }

    const goToPrevious = () => {
        if(competencies){
            if(!showRecap){
                const idx = competencies.findIndex(c => c?.id === activeComp?.id);
                if(idx > 0){
                    setActiveComp(competencies[idx - 1]);
                }
            }else{
                setShowRecap(false);
                setActiveComp(competencies[competencies.length - 1]);
            }
        }
    }

    const [selectedSafetyGrade, setSelectedSafetyGrade] = useState<SAFETY_GRADES | undefined>(undefined);
    const [selectedGrade, setSelectedGrading] = useState<GRADES | undefined>(undefined);
    const [notes, setNotes] = useState<string>('');

    useEffect(() => {
        // Only persist a per-session draft once Observable Behaviours are available
        // (competencies present) and the user has made at least one change.
        if (!executionId || !competencies || competencies.length === 0 || !dirty) {
            return;
        }

        gradingsUIStore.saveEditorDraft(executionId, {
            competencies,
            gradings,
            activeCompId: activeComp?.id,
            showRecap,
            selection: activeComp
                ? { compId: activeComp.id, grade: selectedGrade, safetyGrade: selectedSafetyGrade, notes }
                : undefined,
            remoteVersion: gradingsUIStore.remoteGradingVersion,
        });
    }, [executionId, competencies, gradings, activeComp, showRecap, dirty, selectedGrade, selectedSafetyGrade, notes]);

    const onStepCheckClick = (obId: string, stepIndex: number) => {
        const updatedComps = Array.from(competencies!);
        const current = updatedComps.find(c => c?.id == activeComp?.id);
        if(current){
            const ob = current.behaviours.find(b => b.obId == obId);
            if(ob){
                // Index into the behaviour's steps directly: a step can repeat the
                // same stepId for distinct observations sharing an ob_id, so we can't
                // resolve it by stepId alone.
                const step = ob.steps[stepIndex];
                if(step){
                    step.obDetected = !step.obDetected;
                }
            }
        }
        setDirty(true);
        setCompetencies(updatedComps);
    }

    const persistActiveCompetency = () => {
        if (activeComp) {
            setCompGrading(activeComp.id, selectedSafetyGrade, selectedGrade, notes);
        }
    };

    const selectCompetency = (competencyId: number) => {
        if (!competencies) {
            return;
        }

        persistActiveCompetency();
        const nextCompetency = competencies.find(competency => competency?.id === competencyId);
        if (nextCompetency) {
            setShowRecap(false);
            setActiveComp(nextCompetency);
        }
    };

    const selectRecap = () => {
        persistActiveCompetency();
        setShowRecap(true);
    };

    return (
        <Observer>
            {() =>{
                const data = competencies ? competencies : [];
                const incompleteCompetencies = data.filter(competency => !isCompetencyGraded(competency.id));

                if(data.length > 0){
                    return(
                        <div className={styles.root}>
                            <div className={styles.header}>
                                {
                                    data.map((c, i) =>{
                                        const isActive = activeComp?.id === c?.id;
                                        const activeIdx = data.findIndex(c => c?.id == activeComp?.id);
                                        const isGraded = isCompetencyGraded(c!.id);
                                        const isConfirmed = isGraded && ((i < activeIdx && i != activeIdx) || showRecap);
                                        const isNotGraded = !isGraded && !isActive;

                                        return (
                                            <div
                                                key={i}
                                                className={styles.stepWrap}
                                                role="tab"
                                                aria-selected={isActive}
                                                aria-current={isActive ? "step" : undefined}
                                                tabIndex={0}
                                                onClick={() => selectCompetency(c!.id)}
                                                onKeyDown={event => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        selectCompetency(c!.id);
                                                    }
                                                }}
                                            >
                                                <div className={classNames(styles.circle, (isActive || isConfirmed) && styles.activeCircle, isNotGraded && styles.notGradedCircle)}>
                                                    {isConfirmed ? (<img src={CompletedIcon} />) : (i + 1)}
                                                </div>
                                                <div className={classNames(styles.label, (isActive || isConfirmed) && styles.activeLabel, isNotGraded && styles.notGradedLabel)}>
                                                    {c?.acronym ? c.acronym : toAcronym(c!.title)}
                                                </div>
                                                <div className={styles.line} />
                                            </div>
                                        )
                                    })
                                }
                                <div
                                    className={classNames(styles.recapWrap, showRecap && styles.recapWrapActive)}
                                    role="tab"
                                    aria-selected={showRecap}
                                    aria-current={showRecap ? "step" : undefined}
                                    tabIndex={0}
                                    onClick={selectRecap}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            selectRecap();
                                        }
                                    }}
                                >
                                    <div className={`${styles.circle} ${showRecap ? styles.activeCircle : ""}`}>
                                        H
                                    </div>

                                    <div className={`${styles.label} ${showRecap ? styles.activeLabel : ""}`}>
                                        Recap
                                    </div>
                                </div>
                            </div>
                            <div className={styles.body}>
                                {!showRecap &&
                                    <div className={styles.compDataWrapper}>
                                        <div className={styles.competencyWrapper}>
                                            <div className={styles.compHeaderContainer}>
                                                <div className={styles.compTitleContainer}>
                                                    <b>
                                                        {`${activeComp?.id}. ${activeComp?.title}`}
                                                    </b>
                                                </div>
                                                <div className={styles.compCounterContainer}>
                                                    <b>
                                                        {`${activeComp?.behaviours.filter(b => b.steps.some(s => s.obDetected)).length}`}
                                                    </b>
                                                    <p>
                                                        {`/${activeComp?.behaviours.length}`}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className={styles.compDescriptionContainer}>
                                                {activeComp?.description ? activeComp?.description : 'No Description'}
                                            </div>
                                            <div className={styles.gradingContainer}>
                                                <b>
                                                    Threat and error managment
                                                </b>
                                                <div
                                                    className={styles.itemList}
                                                    role="radiogroup"
                                                    aria-label="Threat and error management"
                                                >
                                                    {SAFETY_GRADES_OPTIONS.map(opt => (
                                                        <div
                                                            key={opt.value}
                                                            className={`${styles.temRow} ${selectedSafetyGrade === opt.value ? styles.isChecked : ""}`}
                                                            role="radio"
                                                            aria-checked={selectedSafetyGrade === opt.value}
                                                            tabIndex={selectedSafetyGrade === opt.value ? 0 : -1}
                                                            onClick={() => { setSelectedSafetyGrade(opt.value); setDirty(true); }}
                                                        >
                                                            <span className={styles.temRadio} aria-hidden="true" />
                                                            <span className={styles.temText}>{opt.label}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className={styles.gradingContainer}>
                                                <b>
                                                    Grading
                                                </b>
                                                <div
                                                    className={styles.itemList}
                                                    role="radiogroup"
                                                    aria-label="Threat and error management"
                                                >
                                                    {GRADES_OPTIONS.map(opt => (
                                                        <div
                                                            key={opt.value}
                                                            className={`${styles.temRow} ${selectedGrade === opt.value ? styles.isChecked : ""}`}
                                                            role="radio"
                                                            aria-checked={selectedGrade === opt.value}
                                                            tabIndex={selectedGrade === opt.value ? 0 : -1}
                                                            onClick={() => { setSelectedGrading(opt.value); setDirty(true); }}
                                                        >
                                                            <span className={styles.temRadio} aria-hidden="true" />
                                                            <span className={styles.temText}>{opt.label}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className={styles.notesContainer}>
                                                <b>
                                                    Notes
                                                </b>
                                                <textarea
                                                    className={styles.textInput}
                                                    value={notes}
                                                    placeholder={'insert notes here'}
                                                    onChange={e => { setNotes(e.target.value); setDirty(true); }}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.behavioursContainer}>
                                            {activeComp?.behaviours.map((b: GradingBehaviour, i: number) =>
                                                (<BehaviourRow key={i} b={b} onStepCheck={onStepCheckClick}/>)
                                            )}
                                        </div>
                                    </div>
                                }
                                {showRecap &&
                                    data.map(c => {
                                        if(c){

                                            const safetyGrade = Object.entries(gradings).find(g => +g[0] == c.id)?.[1].safetyGrade;
                                            const grade = Object.entries(gradings).find(g => +g[0] == c.id)?.[1].grade;
                                            const notes = Object.entries(gradings).find(g => +g[0] == c.id)?.[1].notes;

                                            return (
                                                <div key={c.id} className={styles.compDataWrapper}>
                                                    <div className={styles.competencyWrapper}>
                                                        <div className={styles.compHeaderContainer}>
                                                            <div className={styles.compTitleContainer}>
                                                                <b>
                                                                    {`${c.id}. ${c?.title}`}
                                                                </b>
                                                            </div>
                                                            <div className={styles.compCounterContainer}>
                                                                <b>
                                                                    {`${c.behaviours.filter(b => b.steps.some(s => s.obDetected)).length}`}
                                                                </b>
                                                                <p>
                                                                    {`/${c.behaviours.length}`}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <div className={styles.compDescriptionContainer}>
                                                            {c?.description ? c?.description : 'No Description'}
                                                        </div>
                                                        <div className={styles.gradingContainer}>
                                                            <b>
                                                                Threat and error managment
                                                            </b>
                                                            <div
                                                                className={classNames(
                                                                    styles.gradingValueContainer,
                                                                    safetyGrade == undefined && styles.noValue
                                                                )}
                                                                style={{
                                                                    backgroundColor: safetyGrade != undefined ? getGradingColor(safetyGrade) : '',
                                                                    color: safetyGrade != undefined ? getGradingColor(safetyGrade, true) : '',
                                                                }}
                                                            >
                                                                {safetyGrade != undefined ? toSpaced(SAFETY_GRADES[safetyGrade]) : "Not Set"}
                                                            </div>
                                                        </div>
                                                        <div className={styles.gradingContainer}>
                                                            <b>
                                                                Grading
                                                            </b>
                                                            <div
                                                                className={classNames(
                                                                    styles.gradingValueContainer,
                                                                    grade == undefined && styles.noValue
                                                                )}
                                                                style={{
                                                                    backgroundColor: grade != undefined ? getGradingColor(grade) : '',
                                                                    color: grade != undefined ? getGradingColor(grade, true) : '',
                                                                }}
                                                            >
                                                                {grade != undefined ? toSpaced(GRADES[grade]) : 'Not Set'}
                                                            </div>
                                                        </div>
                                                        <div className={styles.notesContainer}>
                                                            <b>
                                                                Notes
                                                            </b>
                                                            <div className={styles.textInput}>
                                                                {notes}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={styles.behavioursContainer}>
                                                        {c?.behaviours.map((b: GradingBehaviour, i: number) =>
                                                            (<BehaviourRow key={i} b={b}/>)
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        }else{
                                            return;
                                        }
                                    })
                                }
                            </div>
                            <div className={styles.footer}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={styles.cancelButton}
                                    disabled={!hasGrading}
                                    onClick={onCancel}>
                                        Cancel
                                </Button>

                                <div className={styles.selectorControls}>
                                    {showRecap && incompleteCompetencies.length > 0 && (
                                        <div className={styles.incompleteNotice}>
                                            {`${incompleteCompetencies.length} competencies are not graded yet`}
                                        </div>
                                    )}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={styles.backButton}
                                        disabled={competencies && !(competencies.findIndex(c => c?.id === activeComp?.id) > 0)}
                                        onClick={goToPrevious}>
                                            Back
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={styles.nextButton}
                                        disabled={!showRecap ? (selectedGrade == undefined || selectedSafetyGrade == undefined) : incompleteCompetencies.length > 0}
                                        onClick={showRecap ? saveGrading : goToNext}>
                                        {showRecap ? 'Save and confirm' : 'Next'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )
                }else{
                    return(
                        <div className={styles.noDataContainer}>
                            No OBs Data Found
                        </div>
                    )
                }
            }}
        </Observer>
    )
}